import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import Cookies from 'js-cookie';
import { environment } from '../../../environments/environment';
import { AuthStateService } from './auth-state.service';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private authState = inject(AuthStateService);
  private refreshPromise: Promise<string | null> | null = null;

  async refreshToken(): Promise<string | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.doRefreshToken().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async doRefreshToken(): Promise<string | null> {
    // After an explicit logout the refresh cookie may still be valid server-side for a
    // moment; refreshing then would silently re-login the user right after logout.
    if (this.authState.hasLoggedOut) return null;
    try {
      const res = await firstValueFrom(
        this.http.post<any>(`${environment.apiBaseUrl}/member/auth/refresh`, {}, {
          withCredentials: true,
          headers: { '__skipAuthInterceptor': 'true' },
        })
      );
      const member = res?.result ?? {};
      this.authState.currentMember = member;
      return member.token ?? null;
    } catch {
      return null;
    }
  }

  async logout(): Promise<void> {
    // The old PJ waits for the logout POST to finish (finally) before clearing state and
    // navigating, so the server-side session is invalidated first. Skip the interceptor to
    // avoid a 401 -> refresh cycle restoring the session mid-logout; cap the wait so the
    // UI never hangs on a dead connection.
    const token = this.authState.token;
    const headers: Record<string, string> = { '__skipAuthInterceptor': 'true' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      const csrfToken = Cookies.get('csrf_token');
      if (csrfToken) headers['X-CSRF-Token'] = csrfToken;
    }
    const post = firstValueFrom(
      this.http.post(`${environment.apiBaseUrl}/member/logout`, {}, { withCredentials: true, headers })
    ).catch(() => undefined);
    await Promise.race([post, new Promise(resolve => setTimeout(resolve, 3000))]);
    this.authState.logout();
  }
}
